import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useI18n } from '#imports'
import { useContentStudio } from './useContentStudio'
import { useArtists } from './useArtists'

const debugRouteChange = (routeKey: string, label: string) => {
  if (import.meta.client) {
    console.log('Route changed to:', routeKey, 'Text will be:', label)
  }
}

// Global reactive state (singleton)
const customText = ref<string>('')
const isCustomMode = ref<boolean>(false)
const customRouteTexts = ref<Record<string, string>>({})

// Initialize from localStorage only once
if (import.meta.client && !customText.value) {
  const stored = localStorage.getItem('coolstuff-text')
  if (stored) {
    customText.value = stored
    isCustomMode.value = true
  }
}

export const useCoolStuff = () => {
  const route = useRoute()
  const { contentData } = useContentStudio()
  const { getArtistBySlug } = useArtists()
  const { tm } = useI18n()

  const normalizedRouteName = computed(() => {
    const rawName = route.name ? route.name.toString() : ''
    return rawName.split('___')[0] || 'index'
  })

const normalizeMessageMap = (messages: Record<string, unknown>): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(messages).map(([key, value]) => {
      if (typeof value === 'string') {
        return [key, value]
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return [key, String(value)]
      }
      if (value && typeof value === 'object' && 'value' in (value as Record<string, unknown>)) {
        const inner = (value as Record<string, unknown>).value
        return [key, typeof inner === 'string' ? inner : inner != null ? String(inner) : '']
      }
      return [key, value != null ? String(value) : '']
    })
  )
}

const humanizeSlug = (slug: string): string => {
  return slug
    .split(/[-_]/g)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

const defaultRouteTexts = computed<Record<string, string>>(() => {
  const messages = tm('coolStuff.routeTexts') as Record<string, unknown> | undefined
  if (!messages) {
    return {}
  }

  try {
    const clone = JSON.parse(JSON.stringify(messages)) as Record<string, unknown>
    return normalizeMessageMap(clone)
  } catch {
    return normalizeMessageMap(messages)
  }
})

  const mergedRouteTexts = computed<Record<string, string>>(() => ({
    ...defaultRouteTexts.value,
    ...customRouteTexts.value
  }))

  const isHydrated = ref(import.meta.server)

  if (import.meta.client) {
    onMounted(() => {
      isHydrated.value = true
    })
  }
  
  const resolveArtistSlugFromRoute = () => {
    const { slug } = route.params
    if (typeof slug === 'string' && slug.trim()) {
      return slug
    }
    if (Array.isArray(slug) && slug[0] && typeof slug[0] === 'string') {
      return slug[0]
    }
    return undefined
  }
  
  // Computed text based on current mode
  const currentText = computed(() => {
    if (isCustomMode.value && customText.value) {
      return customText.value
    }
    
    // Special case for artist pages - show artist/brand name
    const artistSlug = resolveArtistSlugFromRoute()
    if (artistSlug) {
      const artist = getArtistBySlug(artistSlug)
      if (artist?.name && (import.meta.server || isHydrated.value)) {
          return artist.name
      }
      return humanizeSlug(artistSlug)
    }
    
    // Special case for discover page - check for brand name
    if (normalizedRouteName.value === 'discover') {
      if (contentData.brand && contentData.brand.trim()) {
        return contentData.brand
      }
    }
    
    // Get route-specific text - this will react to route changes
    const routeKey = normalizedRouteName.value
    const routeText = mergedRouteTexts.value[routeKey]
    const fallback = mergedRouteTexts.value.index
    return routeText || fallback || ''
  })
  
  // Watch for route changes to ensure reactivity
  watch(normalizedRouteName, (newName) => {
    debugRouteChange(newName, mergedRouteTexts.value[newName] ?? mergedRouteTexts.value.index ?? '')
    // Force reactivity by accessing the computed value
    currentText.value
  }, { immediate: true })
  
  // Set custom text
  const setCustomText = (text: string) => {
    customText.value = text
    isCustomMode.value = true
    
    if (import.meta.client) {
      localStorage.setItem('coolstuff-text', text)
    }
  }
  
  // Clear custom text (return to route-based)
  const clearCustomText = () => {
    customText.value = ''
    isCustomMode.value = false
    
    if (import.meta.client) {
      localStorage.removeItem('coolstuff-text')
    }
  }
  
  // Set text for specific route
  const setRouteText = (routeKey: string, text: string) => {
    customRouteTexts.value = {
      ...customRouteTexts.value,
      [routeKey]: text
    }
  }
  
  // Get all available routes and their texts
  const getRouteTexts = () => {
    return { ...mergedRouteTexts.value }
  }
  
  // Debug function to check current state
  const debugState = () => {
    if (import.meta.dev) {
      console.log('Current state:', {
        customText: customText.value,
        isCustomMode: isCustomMode.value,
        currentRoute: route.path,
        currentText: currentText.value
      })
    }
  }
  
  return {
    currentText,
    customText: computed(() => customText.value),
    isCustomMode: computed(() => isCustomMode.value),
    setCustomText,
    clearCustomText,
    setRouteText,
    getRouteTexts,
    debugState
  }
}
